'use client'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'

/**
 * Hook de revelação do painel de detalhes do bloco de Listagem Comparativa.
 *
 * Porta de `use-review-details-reveal.ts` do protótipo, mas com a diferença CRÍTICA de SEO
 * (SEO-01 do plano §7): o conteúdo NUNCA é desmontado — fica SEMPRE no DOM, apenas colapsado
 * por CSS (`max-height: 0` + `overflow: hidden`). Por isso este hook NÃO expõe mais `visible`
 * (no protótipo `visible` controlava `if (!visible) return null`, o que escondia o conteúdo dos
 * crawlers). Aqui medimos a altura continuamente e animamos `max-height`.
 *
 * Respeita `prefers-reduced-motion`: a transição é desligada via classe utilitária
 * (`motion-reduce:transition-none`) no componente que consome o `panelHeight`.
 *
 * @param open      painel aberto?
 * @param contentKey chave do conteúdo (re-mede quando troca de item)
 * @returns `{ innerRef, panelHeight }` — `innerRef` no wrapper interno medido; `panelHeight`
 *          é a altura-alvo (scrollHeight) quando aberto, `0` quando fechado.
 */
export function useDetailsReveal(open: boolean, contentKey: string) {
  const innerRef = useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = useState(0)

  // Mede a altura real do conteúdo (sempre montado) e re-mede em resize/troca de conteúdo.
  useLayoutEffect(() => {
    const node = innerRef.current
    if (!node) return

    const measure = () => setContentHeight(node.scrollHeight)

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [contentKey])

  // Ao abrir, garante uma re-medição no próximo frame (caso o conteúdo tenha
  // ficado oculto/zerado enquanto fechado e o ResizeObserver não disparar).
  useEffect(() => {
    if (!open) return
    const node = innerRef.current
    if (!node) return

    const frame = requestAnimationFrame(() => {
      if (innerRef.current) setContentHeight(innerRef.current.scrollHeight)
    })
    return () => cancelAnimationFrame(frame)
  }, [open, contentKey])

  return { innerRef, panelHeight: open ? contentHeight : 0 }
}
